/** Sidebar width: clamped, remembered between visits, driven by the drag grip. */

import { useCallback, useEffect, useState } from "react";

/** Narrower than this and titles stop being readable at all. */
export const SIDEBAR_MIN_WIDTH = 200;
/** Wider than this and the transcript loses its comfortable measure. */
export const SIDEBAR_MAX_WIDTH = 520;
export const SIDEBAR_DEFAULT_WIDTH = 268;
/** Collapsed, only the icon rail remains. */
export const SIDEBAR_RAIL_WIDTH = 56;
/** One arrow-key press. */
export const SIDEBAR_WIDTH_STEP = 16;

const STORAGE_KEY = "szero:sidebar-width";

export function clampSidebarWidth(width: number): number {
  if (!Number.isFinite(width)) return SIDEBAR_DEFAULT_WIDTH;
  return Math.min(
    SIDEBAR_MAX_WIDTH,
    Math.max(SIDEBAR_MIN_WIDTH, Math.round(width)),
  );
}

function readStoredWidth(): number {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === null
      ? SIDEBAR_DEFAULT_WIDTH
      : clampSidebarWidth(Number(stored));
  } catch {
    // Storage can be blocked outright (private windows, strict settings).
    // A sidebar that forgets its width is far better than one that throws.
    return SIDEBAR_DEFAULT_WIDTH;
  }
}

interface SidebarWidth {
  readonly width: number;
  readonly setWidth: (width: number) => void;
  readonly reset: () => void;
}

export function useSidebarWidth(): SidebarWidth {
  const [width, setStoredWidth] = useState(readStoredWidth);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(width));
    } catch {
      // See above — persistence is a nicety, not a requirement.
    }
  }, [width]);

  const setWidth = useCallback((next: number) => {
    setStoredWidth(clampSidebarWidth(next));
  }, []);

  const reset = useCallback(() => {
    setStoredWidth(SIDEBAR_DEFAULT_WIDTH);
  }, []);

  return { width, setWidth, reset };
}
